/**
 * Action Tracker Store
 *
 * Global state for tracking long-running actions (enrich, outreach, export).
 * Shows persistent toast notifications so users can navigate freely while
 * actions complete in the background.
 *
 * Persistence: sessionStorage with 1h TTL — survives page reloads within
 * the same tab, cleared when tab closes.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { useShallow } from 'zustand/react/shallow';

export type ActionType = 'enrich' | 'outreach' | 'export';
export type ActionStatus = 'pending' | 'running' | 'completed' | 'failed' | 'timeout';

export interface TrackedAction {
  id: string;
  type: ActionType;
  status: ActionStatus;
  leadIds: string[];
  leadNames: string[];
  campaignId: string;
  campaignName: string;
  leadCount: number;
  startedAt: number;
  completedAt: number | null;
  targetStatus: string;
  toastId: string | null;
  notificationId: string | null;
  error: string | null;
}

interface ActionTrackerState {
  actions: Record<string, TrackedAction>;
}

interface ActionTrackerActions {
  startAction: (params: {
    type: ActionType;
    leadIds: string[];
    leadNames?: string[];
    campaignId: string;
    campaignName: string;
    leadCount: number;
    targetStatus: string;
  }) => string;

  updateAction: (id: string, updates: Partial<TrackedAction>) => void;
  completeAction: (id: string) => void;
  failAction: (id: string, error: string) => void;
  timeoutAction: (id: string) => void;
  removeAction: (id: string) => void;
  clearCompleted: () => void;
  setToastId: (actionId: string, toastId: string) => void;
}

export type ActionTrackerStore = ActionTrackerState & ActionTrackerActions;

const MAX_TRACKED_ACTIONS = 20;
const ONE_HOUR = 60 * 60 * 1000;

export const useActionTrackerStore = create<ActionTrackerStore>()(
  persist(
    immer((set, get) => ({
      actions: {},

      startAction: (params) => {
        const id = `action_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        const action: TrackedAction = {
          id,
          type: params.type,
          status: 'pending',
          leadIds: params.leadIds,
          leadNames: params.leadNames ?? [],
          campaignId: params.campaignId,
          campaignName: params.campaignName,
          leadCount: params.leadCount,
          startedAt: Date.now(),
          completedAt: null,
          targetStatus: params.targetStatus,
          toastId: null,
          notificationId: null,
          error: null,
        };

        set((state) => {
          state.actions[id] = action;

          // Prune old completed/failed actions if over limit
          const entries = Object.entries(state.actions);
          if (entries.length > MAX_TRACKED_ACTIONS) {
            const toRemove = entries
              .filter(([_, a]) => a.status === 'completed' || a.status === 'failed')
              .sort(([_, a], [__, b]) => (a.completedAt ?? 0) - (b.completedAt ?? 0))
              .slice(0, entries.length - MAX_TRACKED_ACTIONS);

            for (const [removeId] of toRemove) {
              delete state.actions[removeId];
            }
          }
        });

        return id;
      },

      updateAction: (id, updates) => {
        set((state) => {
          const action = state.actions[id];
          if (!action) return;
          Object.assign(action, updates);
        });
      },

      completeAction: (id) => {
        set((state) => {
          const action = state.actions[id];
          if (!action) return;
          action.status = 'completed';
          action.completedAt = Date.now();
        });
      },

      failAction: (id, error) => {
        set((state) => {
          const action = state.actions[id];
          if (!action) return;
          action.status = 'failed';
          action.completedAt = Date.now();
          action.error = error;
        });
      },

      timeoutAction: (id) => {
        set((state) => {
          const action = state.actions[id];
          if (!action) return;
          action.status = 'timeout';
        });
      },

      removeAction: (id) => {
        set((state) => {
          delete state.actions[id];
        });
      },

      clearCompleted: () => {
        set((state) => {
          for (const [id, action] of Object.entries(state.actions)) {
            if (action.status === 'completed' || action.status === 'failed') {
              delete state.actions[id];
            }
          }
        });
      },

      setToastId: (actionId, toastId) => {
        set((state) => {
          const action = state.actions[actionId];
          if (!action) return;
          action.toastId = toastId;
        });
      },
    })),
    {
      name: 'action-tracker',
      storage: createJSONStorage(() => sessionStorage),
      // Only persist pending/running actions
      partialize: (state) => ({
        actions: Object.fromEntries(
          Object.entries(state.actions).filter(
            ([_, a]) => (a as TrackedAction).status === 'pending' || (a as TrackedAction).status === 'running'
          )
        ),
      }),
      // On hydration: mark all persisted actions as timeout.
      // They were running before reload — we can't know if they completed.
      // Leaving them as "running" causes polling loops that freeze the browser.
      merge: (persisted, current) => {
        const persistedState = persisted as ActionTrackerState;
        if (!persistedState?.actions) return current;

        const now = Date.now();
        const filtered = Object.fromEntries(
          Object.entries(persistedState.actions)
            .filter(([_, a]) => now - a.startedAt < ONE_HOUR)
            .map(([id, a]) => [
              id,
              {
                ...a,
                toastId: null,
                status: 'timeout' as ActionStatus,
              },
            ])
        );

        return { ...current, actions: { ...current.actions, ...filtered } };
      },
    }
  )
);

// Selector hooks for optimized re-renders
export const useActiveActions = () =>
  useActionTrackerStore(
    useShallow((state) =>
      Object.values(state.actions).filter(
        (a) => a.status === 'running' || a.status === 'pending' || a.status === 'timeout'
      )
    )
  );

export const useRunningActions = () =>
  useActionTrackerStore(
    useShallow((state) =>
      Object.values(state.actions).filter((a) => a.status === 'running' || a.status === 'pending')
    )
  );

export const useHasActiveAction = (type?: ActionType) =>
  useActionTrackerStore(
    useShallow((state) =>
      Object.values(state.actions).some(
        (a) =>
          (a.status === 'running' || a.status === 'pending') &&
          (!type || a.type === type)
      )
    )
  );

export const useActionsByCampaign = (campaignId: string) =>
  useActionTrackerStore(
    useShallow((state) =>
      Object.values(state.actions).filter((a) => a.campaignId === campaignId)
    )
  );

export const useActionTrackerActions = () =>
  useActionTrackerStore(
    useShallow((state) => ({
      startAction: state.startAction,
      updateAction: state.updateAction,
      completeAction: state.completeAction,
      failAction: state.failAction,
      timeoutAction: state.timeoutAction,
      removeAction: state.removeAction,
      clearCompleted: state.clearCompleted,
      setToastId: state.setToastId,
    }))
  );
